import axios from 'axios';
import { isEmbeddedApp, getShopifySessionToken } from '../utils/shopifyAppBridge';

// Base URL for API requests
const deriveApiBase = () => {
    const prodDefault = 'https://api.autolistify.com';

    // In production, always use the production API URL
    if (process.env.NODE_ENV === 'production') {
        console.log('[API] Using production API URL:', prodDefault);
        return prodDefault;
    }

    // In development, allow override via env vars
    const fromUrl = process.env.REACT_APP_API_URL;
    const fromBase = process.env.REACT_APP_API_BASE_URL;
    const chosen = fromUrl || fromBase || 'http://localhost:3001';
    return String(chosen).replace(/\/$/, ''); // Remove trailing slash
};

const API_BASE_URL = deriveApiBase();

console.log('API Service Initialized with Base URL:', API_BASE_URL);

// Configure axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    timeout: 30000,
});

// Add request interceptor to include Shopify session token for embedded apps
api.interceptors.request.use(
    async (config) => {
        // If running in embedded Shopify app, add session token
        if (isEmbeddedApp()) {
            try {
                const sessionToken = await getShopifySessionToken();
                if (sessionToken) {
                    config.headers['Authorization'] = `Bearer ${sessionToken}`;
                    config.headers['X-Shopify-Session-Token'] = sessionToken;
                    console.log('[Session Token] Added Shopify session token to axios request');
                }
            } catch (error) {
                console.warn('[Session Token] Failed to get session token:', error.message);
                // Fall back to stored token if available
                const storedToken = localStorage.getItem('authToken');
                if (storedToken) {
                    config.headers['Authorization'] = `Bearer ${storedToken}`;
                }
            }
        } else {
            // For non-embedded apps, use stored JWT token
            const storedToken = localStorage.getItem('authToken');
            if (storedToken) {
                config.headers['Authorization'] = `Bearer ${storedToken}`;
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Add response interceptor for global error handling
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Prevent infinite loops
        if (error.response?.status === 401 && !originalRequest._retry && isEmbeddedApp()) {
            originalRequest._retry = true;
            console.log('[API] 401 Unauthorized - Attempting retry with fresh token...');

            try {
                // Force fresh token fetch (bypass cache if possible, though getShopifySessionToken caches)
                // We might need to implement a forceRefresh flag in getShopifySessionToken, 
                // but for now, just calling it again usually handles expiry if built correctly.
                const sessionToken = await getShopifySessionToken();

                if (sessionToken) {
                    api.defaults.headers.common['Authorization'] = `Bearer ${sessionToken}`;
                    originalRequest.headers['Authorization'] = `Bearer ${sessionToken}`;
                    originalRequest.headers['X-Shopify-Session-Token'] = sessionToken;

                    console.log('[API] Retrying request with new token');
                    return api(originalRequest);
                }
            } catch (refreshError) {
                console.error('[API] Token refresh failed:', refreshError);
            }
        }

        console.error('API Request Error:', error.response || error.message);
        return Promise.reject(error);
    }
);

export default api;
